import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ScheduleRepository } from 'repository/scheduleRepository';
import { useAlert } from 'contexts/AlertContext';
import { markOnboardingCompleted } from 'common/utils/onboarding';
import { AppScreen, Badge, Button, PageIntro, Section } from 'components/ds';
import styles from './ScheduleVote.module.css';

interface TimeSlot {
    date: string;
    startTime: string;
    endTime: string;
    availableCount: number;
    voteCount: number;
    proposers: any[];
    voters: string[];
    isRecommended: boolean;
    hasUserVoted: boolean;
}

export const ScheduleVote = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
    const [loading, setLoading] = useState(true);
    const [votingSlotKey, setVotingSlotKey] = useState<string | null>(null);

    // 스크롤 최상단으로
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 시간대별 통계 조회
    useEffect(() => {
        const fetchData = async () => {
            if (meetingId) {
                try {
                    setLoading(true);
                    const response = await ScheduleRepository.onGetTimeSlotStatistics(meetingId);
                    console.log('투표 화면 - 시간대별 통계:', response.data);
                    setTimeSlots(response.data?.data?.timeSlots || []);
                } catch (error) {
                    console.error('통계 조회 실패:', error);
                    showAlert({ message: t('scheduleVote.fetchFail') });
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchData();
    }, [meetingId]);

    // 투표 토글
    const handleVote = async (slot: TimeSlot) => {
        if (!meetingId) return;

        const slotKey = `${slot.date}-${slot.startTime}`;
        setVotingSlotKey(slotKey);

        try {
            await ScheduleRepository.onPostVoteTimeSlot({
                meetingId,
                date: slot.date,
                startTime: slot.startTime.substring(0, 5) // HH:mm:ss -> HH:mm
            });

            markOnboardingCompleted('step_schedule');

            // 투표 후 데이터 다시 조회
            const response = await ScheduleRepository.onGetTimeSlotStatistics(meetingId);
            setTimeSlots(response.data?.data?.timeSlots || []);

            showAlert({
                message: slot.hasUserVoted ? t('scheduleVote.voteCancelled') : t('scheduleVote.voteDone')
            });
        } catch (error) {
            console.error('투표 실패:', error);
            showAlert({ message: t('scheduleVote.voteFail') });
        } finally {
            setVotingSlotKey(null);
        }
    };

    // 날짜 포맷 (locale-aware)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' }).format(date);
    };

    // 요일 표시 (locale-aware short name)
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(date);
    };

    if (loading) {
        return (
            <AppScreen title={t('scheduleVote.title')} bar="sub">
                <p className={styles.state}>{t('common.loading')}</p>
            </AppScreen>
        );
    }

    if (timeSlots.length === 0) {
        return (
            <AppScreen title={t('scheduleVote.title')} bar="sub">
                <div className={styles.empty}>
                    <span className={styles.emptyTitle}>{t('scheduleVote.emptyTitle')}</span>
                    <span className={styles.emptyDesc}>{t('scheduleVote.emptyDesc')}</span>
                    {meetingId && (
                        <div className={styles.emptyAction}>
                            <Button
                                variant="primary"
                                size="lg"
                                block
                                onClick={() => navigate(`/schedule/input/${meetingId}`)}
                            >
                                {t('scheduleVote.addMyTimes')}
                            </Button>
                        </div>
                    )}
                </div>
            </AppScreen>
        );
    }

    return (
        <AppScreen title={t('scheduleVote.title')} bar="sub">
            <PageIntro title={t('scheduleVote.guideTitle')} desc={t('scheduleVote.guideSub')} />

            <Section spacing="tight">
                <ul className={styles.slots}>
                    {timeSlots.map((slot, index) => {
                        const slotKey = `${slot.date}-${slot.startTime}`;
                        const isVoting = votingSlotKey === slotKey;
                        return (
                            <li key={index} className={styles.slot}>
                                <div className={styles.slotTop}>
                                    <span className={styles.slotWhen}>
                                        {formatDate(slot.date)} ({getDayOfWeek(slot.date)}){' '}
                                        {slot.startTime.substring(0, 5)} ~ {slot.endTime.substring(0, 5)}
                                    </span>
                                    {slot.isRecommended && (
                                        <Badge tone="brand">{t('scheduleVote.recommended')}</Badge>
                                    )}
                                </div>

                                <div className={styles.slotMeta}>
                                    {t('scheduleVote.proposedCount', { count: slot.availableCount })}
                                    {' · '}
                                    {t('scheduleVote.voteCount', { count: slot.voteCount })}
                                </div>

                                {slot.proposers && slot.proposers.length > 0 && (
                                    <div className={styles.proposers}>
                                        {t('scheduleVote.proposer')}{' '}
                                        {slot.proposers
                                            .map((p: any) => (p.nickname
                                                ? decodeURIComponent(p.nickname)
                                                : t('scheduleVote.anonUser')))
                                            .join(', ')}
                                    </div>
                                )}

                                <div className={styles.slotAction}>
                                    <Button
                                        variant={slot.hasUserVoted ? 'secondary' : 'primary'}
                                        size="sm"
                                        block
                                        onClick={() => handleVote(slot)}
                                        disabled={isVoting}
                                    >
                                        {isVoting
                                            ? t('scheduleVote.processing')
                                            : slot.hasUserVoted
                                                ? t('scheduleVote.cancel')
                                                : t('scheduleVote.vote')}
                                    </Button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </Section>

            <Section divided last>
                <p className={styles.info}>{t('scheduleVote.changeAnytime')}</p>
            </Section>
        </AppScreen>
    );
};
